// ADR-0041 §7 — Block Bindings: ACF 필드 → 블록 attribute.
//
// 블록의 `metadata.bindings[attr] = { source, args }` 를 만나면 attribute 를
// 바인딩 값으로 치환한다. 렌더 직전 pure transform — renderBlock 시그니처 불변.
//
// canonical source id = `roottale/field`. WP round-trip 위해 `acf/field` alias
// 인식. resolver 는 주입(렌더러는 cms-fields 비종속) — 값 record 만 흐른다.

import type { Block } from "./block";

export interface BlockBinding {
  source: string;
  args?: Record<string, unknown>;
}

/**
 * 바인딩 해석기. 주어진 binding(+attribute/block)에 대한 값을 반환.
 * `undefined` 반환 = 미해석(원래 attribute 유지).
 */
export type BindingResolver = (
  binding: BlockBinding,
  attribute: string,
  block: Block,
) => unknown;

/** ACF 필드 바인딩 source id. */
export const FIELD_BINDING_SOURCE = "roottale/field";
/** WP ACF 호환 alias (import round-trip). */
export const FIELD_BINDING_SOURCE_ALIAS = "acf/field";

/**
 * acf 값 record 로부터 필드 바인딩 resolver 생성.
 * `roottale/field` / `acf/field` source 의 `args.name` 을 record 에서 조회.
 */
export function createFieldBindingResolver(
  fields: Record<string, unknown> | undefined | null,
): BindingResolver {
  const values = fields ?? {};
  return (binding) => {
    if (
      binding.source !== FIELD_BINDING_SOURCE &&
      binding.source !== FIELD_BINDING_SOURCE_ALIAS
    ) {
      return undefined;
    }
    const name = binding.args?.name;
    if (typeof name !== "string") return undefined;
    const v = values[name];
    return v === undefined ? undefined : v;
  };
}

/** 여러 resolver 를 순서대로 시도(첫 non-undefined 채택). */
export function chainResolvers(
  ...resolvers: BindingResolver[]
): BindingResolver {
  return (binding, attribute, block) => {
    for (const r of resolvers) {
      const v = r(binding, attribute, block);
      if (v !== undefined) return v;
    }
    return undefined;
  };
}

function applyToBlock(block: Block, resolver: BindingResolver): Block {
  const bindings = block.metadata?.bindings;
  let attributes = block.attributes;
  if (bindings) {
    let mutated = false;
    const next = { ...block.attributes };
    for (const [attr, binding] of Object.entries(bindings)) {
      const value = resolver(binding, attr, block);
      if (value !== undefined) {
        next[attr] = value;
        mutated = true;
      }
    }
    if (mutated) attributes = next;
  }
  const innerBlocks = block.innerBlocks.length
    ? block.innerBlocks.map((b) => applyToBlock(b, resolver))
    : block.innerBlocks;
  if (attributes === block.attributes && innerBlocks === block.innerBlocks) {
    return block;
  }
  return { ...block, attributes, innerBlocks };
}

/**
 * 블록 트리에 바인딩을 적용해 새 트리 반환(immutable). 바인딩 없으면 원본 그대로.
 */
export function applyBlockBindings(
  blocks: readonly Block[],
  resolver: BindingResolver,
): Block[] {
  return blocks.map((b) => applyToBlock(b, resolver));
}

// ── 라이브 Tiptap 렌더 경로용 헬퍼 (cms-field-binding-editor.md D3·D4) ─────────
//
// Tiptap doc 의 node.attrs.bindings (BlockBindings extension 이 기록, Block JSON
// metadata.bindings 와 동일 형태) 를 렌더 시점에 필드 값으로 치환할 때 사용.

/**
 * bindings 에서 `attribute` 의 필드 값을 조회. `roottale/field`/`acf/field`
 * source 만 해석. 빈 값(undefined/null/"") = undefined → caller 가 fallback 렌더.
 */
export function boundFieldValue(
  bindings: unknown,
  attribute: string,
  fields: Record<string, unknown> | null | undefined,
): unknown {
  if (!bindings || typeof bindings !== "object" || !fields) return undefined;
  const binding = (bindings as Record<string, BlockBinding | undefined>)[
    attribute
  ];
  if (
    !binding ||
    (binding.source !== FIELD_BINDING_SOURCE &&
      binding.source !== FIELD_BINDING_SOURCE_ALIAS)
  ) {
    return undefined;
  }
  const name = binding.args?.name;
  if (typeof name !== "string") return undefined;
  const value = fields[name];
  if (value === undefined || value === null || value === "") return undefined;
  return value;
}

/**
 * content 바인딩 값의 텍스트화. **항상 plain text** — caller 가 escape 해
 * 삽입한다 (바인딩 경로로 raw HTML 진입 금지, D4). string/number 외 = undefined.
 */
export function bindingTextValue(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

/**
 * image `src` 바인딩 값 → URL. 포맷된 MediaRef(`{url}`) 또는 http(s) 문자열만
 * 인정 — raw media id(uuid) 는 URL 이 아니므로 fallback.
 */
export function bindingSrcValue(value: unknown): string | undefined {
  if (value && typeof value === "object") {
    const url = (value as { url?: unknown }).url;
    if (typeof url === "string" && /^https?:\/\//i.test(url)) return url;
    return undefined;
  }
  if (typeof value === "string" && /^https?:\/\//i.test(value)) return value;
  return undefined;
}
